import React, { useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import { Box, Button, Container, Stack, TextField } from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  UpdateVoucherPayload,
  UpdateVoucherResponse,
  Voucher,
  createVoucher,
  getVoucher,
  updateVoucher,
} from '~/services/PromoService';
import UsersService from '~/services/UsersService';

type RouteParams = {
  voucherId?: string;
};

export default function VoucherFormPage() {
  const { voucherId } = useParams<RouteParams>();
  const isEdit = Boolean(voucherId);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const tenant = useCurrentTenant();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [voucherName, setVoucherName] = useState('');
  const [expiredAt, setExpiredAt] = useState('');
  const [errors, setErrors] = useState<{ userEmail?: string; voucherName?: string }>({});

  const { data: voucherResponse, isLoading: isVoucherLoading } = useTanstackQuery({
    queryKey: ['voucher', voucherId],
    queryFn: async () => {
      if (!voucherId) {
        return null;
      }
      return await getVoucher(voucherId);
    },
    enabled: Boolean(voucherId),
  });

  const voucher: Voucher | null = useMemo(() => {
    return voucherResponse?.result ?? null;
  }, [voucherResponse]);

  const { data: userById } = useTanstackQuery({
    queryKey: ['voucher-user', userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      return await UsersService.getUser(userId);
    },
    enabled: Boolean(userId),
  });

  useEffect(() => {
    if (!isEdit) {
      setUserEmail('');
      setUserId('');
      setVoucherName('');
      setExpiredAt('');
      setErrors({});
      return;
    }
    if (voucher) {
      setUserId(voucher.user_id || '');
      setVoucherName(voucher.voucher_name || '');
      setExpiredAt(voucher.expired_at || '');
    }
  }, [isEdit, voucher]);

  useEffect(() => {
    if (userById?.email && !userEmail) {
      setUserEmail(userById.email);
    }
  }, [userById, userEmail]);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: async (payload) => createVoucher(payload),
    onSuccess: () => {
      enqueueSnackbar('Created voucher successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      history.push('/marketing/vouchers');
    },
    onError: (err) => {
      enqueueSnackbar(`Failed to create voucher: ${err.message}`, { variant: 'error' });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = useMutation<UpdateVoucherResponse, Error, { id: string; payload: UpdateVoucherPayload }>({
    mutationFn: async ({ id, payload }) => updateVoucher(id, payload),
    onSuccess: () => {
      enqueueSnackbar('Updated voucher successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      history.push('/marketing/vouchers');
    },
    onError: (err) => {
      enqueueSnackbar(`Failed to update voucher: ${err.message}`, { variant: 'error' });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const validateForm = () => {
    const newErrors: { userEmail?: string; voucherName?: string } = {};
    if (!userEmail.trim()) {
      newErrors.userEmail = 'User email is required';
    }
    if (!voucherName.trim()) {
      newErrors.voucherName = 'Voucher name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const email = userEmail.trim().toLowerCase();
      const users = await UsersService.getUsers({ filter: email, limit: 10, brand: tenant.tenant.brand });
      const matchedUser = users.find((user: { email?: string }) => user.email?.toLowerCase() === email);
      const resolvedUserId = matchedUser?.id_member;

      if (!resolvedUserId && !userId) {
        enqueueSnackbar('No user found for that email', { variant: 'error' });
        setIsSubmitting(false);
        return;
      }

      if (isEdit) {
        const payload: UpdateVoucherPayload = {
          ...(resolvedUserId ? { user_id: resolvedUserId } : userId ? { user_id: userId } : {}),
          ...(voucherName.trim() ? { voucher_name: voucherName.trim() } : {}),
          ...(expiredAt.trim() ? { expired_at: expiredAt.trim() } : {}),
        };
        updateMutation.mutate({ id: voucherId || '', payload });
      } else {
        const payload: CreateVoucherPayload = {
          user_id: resolvedUserId || '',
          voucher_name: voucherName.trim(),
          ...(expiredAt.trim() ? { expired_at: expiredAt.trim() } : {}),
        };
        createMutation.mutate(payload);
      }
    } catch (error) {
      enqueueSnackbar('Failed to lookup user by email', { variant: 'error' });
      setIsSubmitting(false);
    }
  };

  if (isEdit && isVoucherLoading) {
    return <Spinner />;
  }

  return (
    <Container maxWidth="xl">
      <PageHeader title={isEdit ? 'Edit Voucher' : 'Create Voucher'}>
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={() => history.push('/marketing/vouchers')} disabled={isSubmitting}>
            Back to list
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save' : 'Create'}
          </Button>
        </Stack>
      </PageHeader>

      <Box mt={3}>
        <Stack spacing={2}>
          <TextField
            label="User Email"
            value={userEmail}
            onChange={(e) => {
              setUserEmail(e.target.value);
              if (errors.userEmail && e.target.value.trim()) {
                setErrors({ ...errors, userEmail: undefined });
              }
            }}
            error={!!errors.userEmail}
            helperText={errors.userEmail || (userId ? `Current user ID: ${userId}` : 'Customer email address')}
            fullWidth
            required
            disabled={isSubmitting}
          />
          <TextField
            label="Voucher Name"
            value={voucherName}
            onChange={(e) => {
              setVoucherName(e.target.value);
              if (errors.voucherName && e.target.value.trim()) {
                setErrors({ ...errors, voucherName: undefined });
              }
            }}
            error={!!errors.voucherName}
            helperText={errors.voucherName || 'Name displayed for the voucher'}
            fullWidth
            required
            disabled={isSubmitting}
          />
          <Stack spacing={1}>
            {expiredAt ? (
              <DateTimeWidget
                label="Expired At"
                value={expiredAt}
                onChange={setExpiredAt}
                format="DD/MM/YYYY hh:mm a"
                disabled={isSubmitting}
              />
            ) : (
              <Button variant="text" onClick={() => setExpiredAt(dayjs().format())} disabled={isSubmitting}>
                Set Expiry Date
              </Button>
            )}
            {expiredAt && (
              <Button variant="text" onClick={() => setExpiredAt('')} disabled={isSubmitting}>
                Clear Expiry Date
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
}
